// src/Components/Modules/HR/ExpenseClaim.js
import React, { useState, useEffect, useMemo } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Alert,
  Stack,
  Avatar,
  TextField,
  InputAdornment,
  MenuItem,
  Divider,
} from "@mui/material";
import {
  FaPlus,
  FaCheck,
  FaTimes,
  FaMoneyBill,
  FaMoneyBillWave,
  FaReceipt,
  FaCalendarAlt,
  FaTag,
  FaFileInvoiceDollar,
  FaSync,
  FaSearch,
  FaFilter,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaUser,
  FaInfoCircle,
  FaDollarSign,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const GradientPaper = styled(Paper)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  padding: theme.spacing(4),
  borderRadius: theme.spacing(3),
  marginBottom: theme.spacing(3),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const StatCard = styled(Card)(({ theme }) => ({
  background: "white",
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.3s ease",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  },
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(2),
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
  transition: "background-color 0.2s ease",
}));

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(0.6, 1.5),
  minWidth: "auto",
  ...(variant === "approve" && {
    background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
    color: "#1b5e20",
    "&:hover": {
      background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)",
      boxShadow: "0 4px 12px rgba(67, 233, 123, 0.3)",
    },
  }),
  ...(variant === "reject" && {
    background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
    color: "#b71c1c",
    "&:hover": {
      background: "linear-gradient(135deg, #fee140 0%, #fa709a 100%)",
      boxShadow: "0 4px 12px rgba(250, 112, 154, 0.3)",
    },
  }),
  ...(variant === "paid" && {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    "&:hover": {
      background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
    },
  }),
}));

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 600,
  padding: theme.spacing(1.2, 3),
  background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
  color: "#1b5e20",
  "&:hover": {
    background: "linear-gradient(135deg, #38f9d7 0%, #43e97b 100%)",
    boxShadow: "0 6px 20px rgba(67, 233, 123, 0.4)",
  },
}));

const ExpenseClaim = () => {
  const [claims, setClaims] = useState([]);
  const [form, setForm] = useState({
    category: "",
    amount: "",
    expense_date: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  const role = Number(localStorage.getItem("who_am_i"));
  const canSubmit = role <= 3;
  const canManage = role <= 2;

  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await AxiosAuthInstance.get("expenses");
      setClaims(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError("Failed to load expense claims");
      setClaims([]);
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    // Validation
    if (!form.category.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please enter a category",
      });
      return;
    }
    if (!form.amount || Number(form.amount) <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Amount",
        text: "Please enter a valid amount",
      });
      return;
    }
    if (!form.expense_date) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select an expense date",
      });
      return;
    }
    if (!form.description.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please enter a description",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Submit Expense Claim?",
      html: `
        <div style="text-align: left; padding: 10px;">
          <p><strong>Category:</strong> ${form.category}</p>
          <p><strong>Amount:</strong> ৳${Number(form.amount).toLocaleString()}</p>
          <p><strong>Date:</strong> ${form.expense_date}</p>
          <p><strong>Description:</strong> ${form.description}</p>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#43e97b",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Submit",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setSubmitLoading(true);
      try {
        const fd = new FormData();
        Object.entries(form).forEach(([k, v]) => fd.append(k, v));
        await AxiosAuthInstance.post("expenses", fd);

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Expense claim submitted successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        setForm({ category: "", amount: "", expense_date: "", description: "" });
        fetchClaims();
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to submit claim";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      } finally {
        setSubmitLoading(false);
      }
    }
  };

  const updateStatus = async (id, status) => {
    // ✅ If rejecting, ask for reason
    if (status === 'reject') {
      const { value: reason } = await Swal.fire({
        title: "Reject Expense Claim",
        input: 'textarea',
        inputLabel: 'Rejection Reason *',
        inputPlaceholder: 'Please provide a reason for rejection...',
        inputAttributes: {
          'aria-label': 'Rejection reason',
          'maxlength': 500
        },
        showCancelButton: true,
        confirmButtonColor: "#f44336",
        cancelButtonColor: "#757575",
        confirmButtonText: "Reject",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (!value) {
            return 'You need to provide a rejection reason!';
          }
        }
      });

      if (!reason) return;

      setActionLoading(id);
      try {
        await AxiosAuthInstance.post(`expenses/${id}/status`, {
          status: 'reject',
          rejection_reason: reason,
        });
        Swal.fire({
          icon: "success",
          title: "Rejected!",
          text: "Expense claim has been rejected.",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchClaims();
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to update status";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      } finally {
        setActionLoading(null);
      }
      return;
    }

    // ✅ For approve/paid, just confirm
    const statusText = status === "approve" ? "approve" : "mark as paid";
    const result = await Swal.fire({
      title: `${status.charAt(0).toUpperCase() + status.slice(1)} Expense Claim?`,
      text: `Are you sure you want to ${statusText} this expense claim?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: status === "approve" ? "#4caf50" : "#667eea",
      cancelButtonColor: "#757575",
      confirmButtonText: `Yes, ${status.charAt(0).toUpperCase() + status.slice(1)}`,
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      setActionLoading(id);
      try {
        await AxiosAuthInstance.post(`expenses/${id}/status`, { status });
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Expense claim ${statusText}d successfully`,
          timer: 2000,
          showConfirmButton: false,
        });
        fetchClaims();
      } catch (err) {
        const errorMsg = err.response?.data?.message || "Failed to update status";
        Swal.fire({
          icon: "error",
          title: "Error",
          text: errorMsg,
        });
      } finally {
        setActionLoading(null);
      }
    }
  };

  const filtered = useMemo(() => {
    if (!Array.isArray(claims)) return [];
    return claims.filter((item) => {
      const category = item.category?.toLowerCase() || "";
      const description = item.description?.toLowerCase() || "";
      const status = item.status?.toLowerCase() || "";
      const searchTerm = search.toLowerCase();

      const matchesSearch =
        category.includes(searchTerm) ||
        description.includes(searchTerm);

      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesCategory =
        categoryFilter === "all" || category === categoryFilter;

      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [claims, search, statusFilter, categoryFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate stats
  const stats = useMemo(() => {
    if (!Array.isArray(claims)) {
      return { total: 0, pending: 0, approved: 0, paid: 0, totalAmount: 0, pendingAmount: 0 };
    }
    return {
      total: claims.length,
      pending: claims.filter((c) => c.status === "pending").length,
      approved: claims.filter((c) => c.status === "approved").length,
      paid: claims.filter((c) => c.status === "paid").length,
      totalAmount: claims.reduce((sum, c) => sum + (Number(c.amount) || 0), 0),
      pendingAmount: claims
        .filter((c) => c.status === "pending")
        .reduce((sum, c) => sum + (Number(c.amount) || 0), 0),
    };
  }, [claims]);

  // Get unique categories
  const categories = useMemo(() => {
    if (!Array.isArray(claims)) return [];
    const cats = [...new Set(claims.map((c) => c.category).filter(Boolean))];
    return cats;
  }, [claims]);

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "৳0";
    return `৳${Number(amount).toLocaleString("en-BD", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const getStatusChip = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "pending") {
      return (
        <Chip
          icon={<FaClock style={{ fontSize: 12 }} />}
          label="Pending"
          size="small"
          sx={{
            bgcolor: "#fff3e0",
            color: "#ef6c00",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#ff9800" },
          }}
        />
      );
    }
    if (statusLower === "approved") {
      return (
        <Chip
          icon={<FaCheckCircle style={{ fontSize: 12 }} />}
          label="Approved"
          size="small"
          sx={{
            bgcolor: "#e8f5e9",
            color: "#2e7d32",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#4caf50" },
          }}
        />
      );
    }
    if (statusLower === "paid") {
      return (
        <Chip
          icon={<FaMoneyBillWave style={{ fontSize: 12 }} />}
          label="Paid"
          size="small"
          sx={{
            bgcolor: "#e3f2fd",
            color: "#1565c0",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#2196f3" },
          }}
        />
      );
    }
    if (statusLower === "rejected" || statusLower === "reject") {
      return (
        <Chip
          icon={<FaTimesCircle style={{ fontSize: 12 }} />}
          label="Rejected"
          size="small"
          sx={{
            bgcolor: "#ffebee",
            color: "#c62828",
            fontWeight: 600,
            "& .MuiChip-icon": { color: "#f44336" },
          }}
        />
      );
    }
    return (
      <Chip
        label={status || "N/A"}
        size="small"
        sx={{ bgcolor: "#f5f5f5", color: "#757575", fontWeight: 600 }}
      />
    );
  };

  const getCategoryIcon = (category) => {
    const catLower = category?.toLowerCase();
    if (catLower?.includes("travel") || catLower?.includes("transport")) {
      return <FaReceipt />;
    }
    if (catLower?.includes("food") || catLower?.includes("meal")) {
      return <FaReceipt />;
    }
    if (catLower?.includes("office") || catLower?.includes("supplies")) {
      return <FaReceipt />;
    }
    return <FaFileInvoiceDollar />;
  };

  const getCategoryColor = (category) => {
    const colors = [
      { bg: "#e3f2fd", color: "#1976d2" },
      { bg: "#f3e5f5", color: "#7b1fa2" },
      { bg: "#e8f5e9", color: "#2e7d32" },
      { bg: "#fff3e0", color: "#ef6c00" },
      { bg: "#fce4ec", color: "#c2185b" },
      { bg: "#e0f2f1", color: "#00796b" },
    ];
    if (!category) return colors[0];
    let hash = 0;
    for (let i = 0; i < category.length; i++) {
      hash = category.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  if (loading && claims.length === 0) {
    return (
      <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
        <Breadcrumb title="Expense Claims" />
        <GradientPaper>
          <Skeleton variant="text" width="60%" height={60} />
          <Skeleton variant="text" width="40%" />
        </GradientPaper>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="Expense Claims" />

      {/* Header */}
      <GradientPaper elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", sm: "space-between" },
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: { xs: "center", sm: "flex-start" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaMoneyBillWave style={{ marginRight: 10, verticalAlign: "middle" }} />
              Expense Claims
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Submit and manage expense claims efficiently
            </Typography>
          </Box>
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={fetchClaims}
              sx={{
                bgcolor: "rgba(255,255,255,0.2)",
                color: "white",
                "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
              }}
            >
              <FaSync />
            </IconButton>
          </Tooltip>
        </Box>
      </GradientPaper>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#e3f2fd",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaFileInvoiceDollar size={28} color="#2196f3" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#1565c0" sx={{ mb: 0.5 }}>
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Total Claims
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#fff3e0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaClock size={28} color="#ff9800" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#ef6c00" sx={{ mb: 0.5 }}>
              {stats.pending}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Pending
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#e8f5e9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaCheckCircle size={28} color="#4caf50" />
            </Box>
            <Typography variant="h3" fontWeight={700} color="#2e7d32" sx={{ mb: 0.5 }}>
              {stats.approved}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Approved
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={0}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                bgcolor: "#f3e5f5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <FaMoneyBillWave size={28} color="#7b1fa2" />
            </Box>
            <Typography variant="h4" fontWeight={700} color="#7b1fa2" sx={{ mb: 0.5 }}>
              {formatCurrency(stats.totalAmount)}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Total Amount
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* Submit New Claim Form */}
      {canSubmit && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            <FaPlus style={{ marginRight: 8, verticalAlign: "middle" }} />
            Submit New Expense Claim
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Category"
                placeholder="e.g., Travel, Office Supplies"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaTag style={{ color: "#757575" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                placeholder="0.00"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaDollarSign style={{ color: "#757575" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Expense Date"
                value={form.expense_date}
                onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FaCalendarAlt style={{ color: "#757575" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Description"
                placeholder="Brief description of expense"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                variant="outlined"
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <PrimaryButton
                fullWidth
                variant="contained"
                onClick={submit}
                disabled={submitLoading}
                startIcon={submitLoading ? null : <FaPlus />}
                sx={{ height: 40 }}
              >
                {submitLoading ? "Submitting..." : "Submit"}
              </PrimaryButton>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
          <TextField
            placeholder="Search by category, description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch style={{ color: "#757575" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          />
          <TextField
            select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            variant="outlined"
            size="small"
            sx={{
              minWidth: 150,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                bgcolor: "background.default",
              },
            }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
          {categories.length > 0 && (
            <TextField
              select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              variant="outlined"
              size="small"
              sx={{
                minWidth: 150,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "background.default",
                },
              }}
            >
              <MenuItem value="all">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>
                  {cat}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <StyledTableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#fafafa" }}>
              <StyledTableCell width={80}>SL</StyledTableCell>
              <StyledTableCell>Category</StyledTableCell>
              <StyledTableCell>Description</StyledTableCell>
              <StyledTableCell align="right">Amount</StyledTableCell>
              <StyledTableCell>Date</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              {canManage && <StyledTableCell align="center">Actions</StyledTableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from(new Array(5)).map((_, index) => (
                <TableRow key={index}>
                  <TableCell><Skeleton width={40} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={200} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  <TableCell><Skeleton width={100} /></TableCell>
                  <TableCell><Skeleton width={80} /></TableCell>
                  {canManage && <TableCell><Skeleton width={150} /></TableCell>}
                </TableRow>
              ))
            ) : paginatedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 7 : 6} align="center" sx={{ py: 8 }}>
                  <Box sx={{ textAlign: "center" }}>
                    <FaUser style={{ fontSize: 48, color: "#bdbdbd", marginBottom: 16 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No expense claims found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {search || statusFilter !== "all" || categoryFilter !== "all"
                        ? "Try adjusting your filters"
                        : "Submit your first expense claim to get started"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              paginatedData.map((row, index) => {
                const categoryColor = getCategoryColor(row.category);
                const statusLower = row.status?.toLowerCase();
                const isPending = statusLower === "pending";

                return (
                  <StyledTableRow key={row.id || index} hover>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getCategoryIcon(row.category)}
                        label={row.category || "N/A"}
                        size="small"
                        sx={{
                          bgcolor: categoryColor.bg,
                          color: categoryColor.color,
                          fontWeight: 600,
                          textTransform: "capitalize",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.description || "No description"}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 250,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {row.description || (
                            <span style={{ color: "#bdbdbd", fontStyle: "italic" }}>
                              No description
                            </span>
                          )}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight={700} color="#2e7d32">
                        {formatCurrency(row.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <FaCalendarAlt style={{ fontSize: 14, color: "#757575" }} />
                        <Typography variant="body2">
                          {formatDate(row.expense_date)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{getStatusChip(row.status)}</TableCell>
                    {canManage && (
                      <TableCell align="center">
                        {isPending ? (
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Approve">
                              <span>
                                <ActionButton
                                  variant="approve"
                                  size="small"
                                  disabled={actionLoading === row.id}
                                  onClick={() => updateStatus(row.id, "approve")}
                                  startIcon={actionLoading === row.id ? null : <FaCheck />}
                                >
                                  {actionLoading === row.id ? "..." : "Approve"}
                                </ActionButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <span>
                                <ActionButton
                                  variant="reject"
                                  size="small"
                                  disabled={actionLoading === row.id}
                                  onClick={() => updateStatus(row.id, "reject")}
                                  startIcon={actionLoading === row.id ? null : <FaTimes />}
                                >
                                  {actionLoading === row.id ? "..." : "Reject"}
                                </ActionButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Mark as Paid">
                              <span>
                                <ActionButton
                                  variant="paid"
                                  size="small"
                                  disabled={actionLoading === row.id}
                                  onClick={() => updateStatus(row.id, "paid")}
                                  startIcon={actionLoading === row.id ? null : <FaMoneyBill />}
                                >
                                  {actionLoading === row.id ? "..." : "Paid"}
                                </ActionButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Chip
                            label="Processed"
                            size="small"
                            sx={{
                              bgcolor: "#f5f5f5",
                              color: "#757575",
                              fontWeight: 600,
                            }}
                          />
                        )}
                      </TableCell>
                    )}
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {!loading && filtered.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: 1,
              borderColor: "divider",
              "& .MuiTablePagination-toolbar": {
                paddingRight: 2,
              },
            }}
          />
        )}
      </StyledTableContainer>
    </Box>
  );
};

export default ExpenseClaim;